// Reference image bytes and their accepted Site save share one IndexedDB
// transaction. Metadata is separate so storage summaries never load every Blob.
import { validateReferenceUploadCapabilities, validateReferenceUploadReceipt } from './referenceUpload.js';

export const REFERENCE_LOCAL_MAX_BYTES = 128 * 1024 * 1024;
export const REFERENCE_LOCAL_MAX_ASSETS = 500;
export const referenceMetadataKey = (account, id = '') => `reference-upload:${account}/${id}`;
export const referenceBlobKey = (account, id) => `reference-blob:${account}/${id}`;
export const referenceCapabilityKey = account => `reference-capability:${account}`;
const fail = (message, status = 409) => Object.assign(new Error(message), { status });
const hex = bytes => Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
const types = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml']);
const immutableFields = ['account','projectID','imageID','uploadID','filename','mime','size','sha256'];

export function referenceIDs(value, ids = new Set()) {
  if (value && typeof value === 'object') for (const [key, child] of Object.entries(value)) {
    if (['imageId', 'photoId', 'testImageId'].includes(key)) {
      if (child == null || child === '') continue;
      if (typeof child !== 'string' || !/^[a-zA-Z0-9_-]{1,100}$/.test(child)) throw fail('A saved drawing reference is unreadable. Keep this Site and resolve its saved document before uploading.');
      ids.add(child);
    } else if (typeof child === 'object') referenceIDs(child, ids);
  }
  return ids;
}
export function validReferenceDescriptor(value, account, imageID) {
  const valid = value?.kind === 'reference-upload' && value.version === 1 && value.account === account && value.imageID === imageID &&
    /^[a-zA-Z0-9_-]{1,80}$/.test(value.projectID || '') && /^ref_[a-f0-9]{32}$/.test(imageID || '') &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value.uploadID || '') &&
    typeof value.filename === 'string' && !!value.filename.trim() && [...value.filename].length <= 200 && !/[\p{Cc}\p{Cs}]/u.test(value.filename) &&
    types.has(value.mime) && Number.isSafeInteger(value.size) && value.size > 0 && value.size <= 16 * 1024 * 1024 &&
    /^[a-f0-9]{64}$/.test(value.sha256 || '') && ['pending','uploaded','blocked'].includes(value.state);
  if (!valid) return false;
  if (value.state === 'uploaded') {
    try { validateReferenceUploadReceipt({ image: value.receipt }, value); } catch { return false; }
  } else if (value.receipt != null) return false;
  return true;
}
export function referenceAttempt(descriptor, bytes) {
  if (!(bytes instanceof Blob) || bytes.size !== descriptor.size || bytes.type !== descriptor.mime) throw fail('The saved drawing bytes are missing or unreadable. Keep the device copy and recover its original file.');
  return { ...descriptor, accountKey: descriptor.account, bytes, headers: {
    'Content-Type': descriptor.mime, 'X-Avisera-Expected-Account': descriptor.account,
    'X-Content-SHA256': descriptor.sha256, 'X-Filename-UTF8': encodeURIComponent(descriptor.filename),
  } };
}
export async function prepareDurableReference(attempt, account, projectID, assertCurrent = () => {}) {
  assertCurrent();
  const descriptor = { kind: 'reference-upload', version: 1, account, projectID, imageID: attempt?.imageID, uploadID: attempt?.uploadID,
    filename: attempt?.filename, mime: attempt?.mime, size: attempt?.size, sha256: attempt?.sha256, state: 'pending', createdAt: new Date().toISOString() };
  if (attempt?.accountKey !== account || attempt?.projectID !== projectID || !validReferenceDescriptor(descriptor, account, descriptor.imageID)) throw fail('The drawing belongs to another Site or account. Reopen its review.');
  const checked = referenceAttempt(descriptor, attempt.bytes);
  const bytes = checked.bytes.slice(0, checked.size, checked.mime);
  const digest = hex(await crypto.subtle.digest('SHA-256', await bytes.arrayBuffer())); assertCurrent();
  const identity = 'ref_' + hex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${account}/${projectID}/${descriptor.uploadID}`))).slice(0, 32); assertCurrent();
  if (digest !== descriptor.sha256 || identity !== descriptor.imageID) throw fail('The drawing upload identity or bytes changed. Reopen its review with the original file.');
  return { descriptor: { ...descriptor, headers: checked.headers }, bytes };
}

export function referenceMetadata(database, account) {
  return new Promise((resolve, reject) => {
    const prefix = referenceMetadataKey(account), values = [], tx = database.transaction('meta', 'readonly');
    const request = tx.objectStore('meta').openCursor(IDBKeyRange.bound(prefix, prefix + '\uffff'));
    request.onsuccess = () => { const cursor = request.result; if (cursor) { values.push({ key: cursor.key, value: cursor.value }); cursor.continue(); } };
    tx.oncomplete = () => resolve(values);
    tx.onabort = tx.onerror = () => reject(tx.error || request.error || fail('Drawing storage could not be read.'));
  });
}
export function summarizeReferences(entries, account) {
  const summary = { bytes: 0, count: 0, pending: 0, bySite: {} };
  for (const { key, value } of entries) {
    if (!validReferenceDescriptor(value, account, key.slice(referenceMetadataKey(account).length))) throw fail('A saved drawing record is unreadable. Its bytes were retained.');
    summary.bytes += value.size; summary.count++;
    const site = summary.bySite[value.projectID] ||= { bytes: 0, count: 0, pending: 0 };
    site.bytes += value.size; site.count++;
    if (value.state !== 'uploaded') { summary.pending++; site.pending++; }
  }
  return summary;
}

export function commitDurableReference(database, { account, projectID, body, offlineVersion, prepared, assertCurrent }) {
  // No promises/network/crypto inside the transaction: every validation and all
  // writes finish in IndexedDB request callbacks before its atomic completion.
  return new Promise((resolve, reject) => {
    const tx = database.transaction(['sites','meta'], 'readwrite'), sites = tx.objectStore('sites'), meta = tx.objectStore('meta');
    const key = `${account}/${projectID}`, prefix = referenceMetadataKey(account), metadata = [];
    let failure, result, record, storedAccount, capability, remaining = 4;
    const abort = error => { failure = error; try { tx.abort(); } catch {} };
    const finish = () => {
      if (--remaining) return;
      try {
        assertCurrent();
        if (storedAccount?.key !== account) throw fail('Sign in to the account that owns these saved edits.', 401);
        if (!record?.ready || record.revoked || record.account !== account || record.project?.id !== projectID) throw fail('Prepare this Site for offline work before saving a drawing on this device.');
        if (record.generation !== offlineVersion) throw fail('This Site changed in another window. Your drawing review is retained; reopen the saved Site before continuing.');
        validateReferenceUploadCapabilities(capability, prepared.descriptor);
        if (!referenceIDs(body.doc).has(prepared.descriptor.imageID)) throw fail('The reviewed plan does not reference its saved drawing.');
        const usage = summarizeReferences(metadata, account), old = metadata.find(item => item.value.imageID === prepared.descriptor.imageID)?.value;
        if (old && immutableFields.some(field => old[field] !== prepared.descriptor[field])) throw fail('This drawing upload identity is already used by different bytes or metadata.');
        if (!old && (usage.count >= REFERENCE_LOCAL_MAX_ASSETS || usage.bytes + prepared.bytes.size > REFERENCE_LOCAL_MAX_BYTES)) throw fail('Drawing storage is full (500 images or 128 MB per account). Upload pending work and remove unused drawing downloads before importing more.');
        record.doc = structuredClone(body.doc); record.generation++;
        record.pending.push({ doc: structuredClone(body.doc), note: body.note || '', mutationId: crypto.randomUUID(), expectedRev: null });
        record.savedAt = new Date().toISOString();
        sites.put(record); meta.put(old || prepared.descriptor, referenceMetadataKey(account, prepared.descriptor.imageID));
        meta.put(prepared.bytes, referenceBlobKey(account, prepared.descriptor.imageID));
        result = { rev: record.project.rev, offlineVersion: record.generation, offline: true, referenceAccepted: true };
      } catch (error) { abort(error); }
    };
    const read = (request, assign) => { request.onsuccess = () => { assign(request.result); finish(); }; };
    read(sites.get(key), value => { record = value; });
    read(meta.get('account'), value => { storedAccount = value; });
    read(meta.get(referenceCapabilityKey(account)), value => { capability = value; });
    const cursor = meta.openCursor(IDBKeyRange.bound(prefix, prefix + '\uffff'));
    cursor.onsuccess = () => { const item = cursor.result; if (item) { metadata.push({ key: item.key, value: item.value }); item.continue(); } else finish(); };
    tx.oncomplete = () => resolve(result);
    tx.onabort = tx.onerror = () => reject(failure || tx.error || fail('The drawing and plan could not be saved on this device. Keep the review open and retry.'));
  });
}
